import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';
import { setAppNavigate } from '@/lib/navigation/appNavigate';

interface UnsavedChangesRegistry {
  [key: string]: () => boolean; // page identifier -> hasUnsavedChanges function
}

interface NavigationContextType {
  interceptedNavigate: NavigateFunction;
  interceptAction: (action: () => void) => void;
  registerUnsavedChanges: (pageId: string, hasUnsavedChanges: () => boolean) => void;
  unregisterUnsavedChanges: (pageId: string) => void;
  showUnsavedDialog: boolean;
  setShowUnsavedDialog: (show: boolean) => void;
  pendingNavigation: (() => void) | null;
  setPendingNavigation: (nav: (() => void) | null) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [unsavedChangesRegistry, setUnsavedChangesRegistry] = useState<UnsavedChangesRegistry>({});
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const navigateRef = useRef(navigate);

  React.useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const registerUnsavedChanges = useCallback((pageId: string, hasUnsavedChanges: () => boolean) => {
    setUnsavedChangesRegistry(prev => ({
      ...prev,
      [pageId]: hasUnsavedChanges,
    }));
  }, []);

  const unregisterUnsavedChanges = useCallback((pageId: string) => {
    setUnsavedChangesRegistry(prev => {
      const next = { ...prev };
      delete next[pageId];
      return next;
    });
  }, []);

  const hasAnyUnsavedChanges = useCallback(() => {
    return Object.values(unsavedChangesRegistry).some(checker => checker());
  }, [unsavedChangesRegistry]);

  const interceptedNavigate = useCallback<NavigateFunction>(
    (to: string | number, options?: any) => {
      if (hasAnyUnsavedChanges()) {
        setShowUnsavedDialog(true);
        setPendingNavigation(() => () => {
          if (typeof to === 'number') {
            navigateRef.current(to);
          } else {
            navigateRef.current(to, options);
          }
        });
        return;
      }

      if (typeof to === 'number') {
        navigateRef.current(to);
      } else {
        navigateRef.current(to, options);
      }
    },
    [hasAnyUnsavedChanges, unsavedChangesRegistry]
  );

  React.useEffect(() => {
    setAppNavigate((to) => interceptedNavigate(to));
    return () => setAppNavigate(null);
  }, [interceptedNavigate]);

  const interceptAction = useCallback(
    (action: () => void) => {
      if (hasAnyUnsavedChanges()) {
        setShowUnsavedDialog(true);
        setPendingNavigation(() => action);
        return;
      }
      action();
    },
    [hasAnyUnsavedChanges]
  );

  return (
    <NavigationContext.Provider
      value={{
        interceptedNavigate,
        interceptAction,
        registerUnsavedChanges,
        unregisterUnsavedChanges,
        showUnsavedDialog,
        setShowUnsavedDialog,
        pendingNavigation,
        setPendingNavigation,
      }}
    >
      {children}
      
      <AlertDialog open={showUnsavedDialog} onOpenChange={(open) => {
        if (!open) {
          setShowUnsavedDialog(false);
          setPendingNavigation(null);
        }
      }}>
        <AlertDialogContent className="flex w-[calc(100%-32px)] sm:w-[min(100%,500px)] max-w-[500px] flex-col gap-[15px] rounded-[16px] border-0 bg-white p-5 shadow-[0_20px_60px_rgba(0,0,0,0.15)] sm:rounded-[16px]">
          <div className="flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[rgba(245,158,11,0.1)]">
              <AlertTriangle className="h-6 w-6 text-[#F59E0B]" strokeWidth={2} />
            </div>
          </div>

          <AlertDialogHeader className="flex flex-col gap-2.5 space-y-0 text-center sm:text-center">
            <AlertDialogTitle className="text-center text-[18px] font-semibold leading-tight tracking-tight text-[#101828]">
              You have unsaved changes
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-center text-[13px] font-normal leading-[1.6] text-[#667085]">
                Your edits haven't been saved yet. If you leave now, they'll be permanently lost.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="border-t border-[#F2F4F7]" />

          <AlertDialogFooter className="flex-row gap-3 sm:justify-end sm:space-x-0">
            <AlertDialogCancel
              onClick={() => {
                setShowUnsavedDialog(false);
                setPendingNavigation(null);
              }}
              className="mt-0 h-auto flex-1 sm:flex-none rounded-[8px] border border-[#e5e7eb] bg-white px-6 py-2.5 text-[14px] font-medium text-[#101828] shadow-none hover:bg-[#F9FAFB] hover:text-[#101828]"
            >
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              variant="ghost"
              onClick={() => {
                setShowUnsavedDialog(false);
                if (pendingNavigation) {
                  pendingNavigation();
                  setPendingNavigation(null);
                }
              }}
              className="h-auto flex-1 sm:flex-none rounded-[8px] border-0 bg-[#ef4444] px-[30px] py-2.5 text-[14px] font-medium text-white shadow-none hover:bg-[#ef4444] hover:text-white hover:opacity-90"
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const useOptionalNavigation = () => useContext(NavigationContext);

